import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAccount, useMsal } from "@azure/msal-react";
import {
  fetchHallAisles,
  fetchHallBooths,
  fetchHealth,
  postPresence,
  callTool,
  ApiError,
} from "./api";
import { AppFooter } from "./components/AppFooter";
import { resolveMapHall } from "./floor/mapHall";
import { startLogout } from "./auth/msal";
import { isNativeShell } from "./auth/platform";
import { accountName, accountOid, fetchGraphPhotoUrl } from "./auth/token";
import { FilterClearButton, FilterFunnelButton } from "./components/BoardFilterControl";
import { BoothSearchField } from "./components/BoothSearchField";
import { SearchTile } from "./components/SearchTile";
import { BadgeScannerModal } from "./components/BadgeScannerModal";
import { AskAiModal } from "./ask-ai/AskAiModal";
import { MainNav, WorkspaceTabs, type WorkspaceTab } from "./components/MainNav";
import { FloorFilterDrawer } from "./floor/FloorFilterDrawer";
import { formatBoothHit, type BoothSearchHit } from "./floor/boothLookup";
import { sharedBoothIdsFromEvents, type AccountEventRow } from "./floor/boothOccupancy";
import { FloorWorkspace, type FloorDestination } from "./floor/FloorWorkspace";
import { ProfileScreen } from "./ProfileScreen";
import { RatingsBoard } from "./RatingsBoard";
import {
  boardFilterCount,
  boardFiltersAreActive,
  clearBoardFilters,
  floorFilterCount,
  floorFiltersAreActive,
  useCampaignFilters,
  useRosterMatchTotal,
} from "./campaignFilters";
import { WorkspaceActiveFilters } from "./components/WorkspaceActiveFilters";
import { CampaignBuilderApp } from "./campaign-builder/CampaignBuilderApp";
import { ContactsScreen } from "./contacts/ContactsScreen";
import { ContactBrowserScreen } from "./contact-browser/ContactBrowserScreen";
import type { ContactBrowserTarget, ContactOpenState } from "./contact-browser/types";
import { AccountCreateScreen } from "./accounts/AccountCreateScreen";
import { CompanyBrowserScreen } from "./company-browser/CompanyBrowserScreen";
import type { CompanyBrowserTarget, CompanyOpenState } from "./company-browser/types";
import { WorkspaceNewButton } from "./components/WorkspaceNewButton";
import {
  CAMPAIGNS,
  DEFAULT_CAMPAIGN_KEY,
  EVENT_KEY,
  formatRosterSearchPlaceholder,
  getStoredDisplayName,
  IRONFLEET_BOOTH,
} from "./constants";
import { pathFromTab, tabFromPath } from "./workspacePath";
import logoUrl from "../imgs/logo_charm.png";
import { usePresence } from "./hooks/usePresence";
import { selfPresence } from "./floor/teamColors";
import { type AisleGraph, type HallBoothCollection, type HealthResult, type LoadState } from "./types";
import { canOfferAppDownload, requestAppDownload } from "./pwa/launchChoice";

function toLoadError(error: unknown, fallback: string): LoadState<never> {
  return {
    status: "error",
    message: error instanceof Error ? error.message : fallback,
  };
}

export default function App() {
  const { instance } = useMsal();
  const account = useAccount();
  const signedInOid = accountOid(account);
  const signedInName = accountName(account);
  const [eventKey] = useState(EVENT_KEY);
  const [campaignKey, setCampaignKey] = useState<string>(DEFAULT_CAMPAIGN_KEY);
  const [selectedBooth, setSelectedBooth] = useState(IRONFLEET_BOOTH);
  const [companyOpen, setCompanyOpen] = useState<CompanyOpenState | null>(null);
  const [contactOpen, setContactOpen] = useState<ContactOpenState | null>(null);
  const [contactsEpoch, setContactsEpoch] = useState(0);

  const [health, setHealth] = useState<LoadState<HealthResult>>({ status: "loading" });
  const [hallId, setHallId] = useState("A");
  const [hallGeo, setHallGeo] = useState<LoadState<HallBoothCollection>>({ status: "loading" });
  const [aisleState, setAisleState] = useState<LoadState<AisleGraph>>({ status: "idle" });

  const [displayName, setDisplayName] = useState(() => getStoredDisplayName() || signedInName);
  const [checkInBooth, setCheckInBooth] = useState(IRONFLEET_BOOTH);
  const [checkInQuery, setCheckInQuery] = useState(IRONFLEET_BOOTH);
  const [destination, setDestination] = useState<FloorDestination | null>(null);
  const [goingToBooth, setGoingToBooth] = useState("");
  const [planEpoch, setPlanEpoch] = useState(0);
  const [graphPhotoUrl, setGraphPhotoUrl] = useState<string | null>(null);
  const [photoTick, setPhotoTick] = useState(0);
  const [mainTab, setMainTab] = useState<WorkspaceTab>(() => tabFromPath());
  const [enterFloorDrawerOpen, setEnterFloorDrawerOpen] = useState(false);
  const [contactQuery, setContactQuery] = useState("");
  const [contactSearching, setContactSearching] = useState(false);
  const [floorSearch, setFloorSearch] = useState("");
  const [sharedBoothIds, setSharedBoothIds] = useState<Set<string>>(() => new Set());
  const floorSearchPickRef = useRef<((hit: BoothSearchHit) => void) | null>(null);
  const [filters, setFilters] = useCampaignFilters();
  const [filterOpen, setFilterOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [askAiOpen, setAskAiOpen] = useState(false);
  const { presence, connected: presenceConnected } = usePresence(eventKey.trim() || EVENT_KEY);
  const actor = account?.username?.trim() || signedInName?.trim() || "web-user@ironfleet.ai";

  function selectTab(tab: WorkspaceTab) {
    if (tab === "floor") setEnterFloorDrawerOpen(false);
    setCompanyOpen((prev) => (prev?.mode === "modal" ? null : prev));
    setMainTab(tab);
    const next = pathFromTab(tab);
    if (window.location.pathname !== next) {
      window.history.pushState({ tab }, "", next);
    }
  }

  useEffect(() => {
    const onPop = () => setMainTab(tabFromPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const loadHealth = useCallback(async () => {
    setHealth({ status: "loading" });
    try {
      const data = await fetchHealth();
      setHealth({ status: "ready", data });
    } catch (error) {
      setHealth(toLoadError(error, "Unable to reach the MCP health endpoint"));
    }
  }, []);

  const loadHallMap = useCallback(async (hall: string) => {
    setHallId(hall);
    setHallGeo({ status: "loading" });
    setAisleState({ status: "loading" });
    try {
      const booths = await fetchHallBooths(hall);
      if (!booths.features.length) {
        setHallGeo({ status: "empty", message: `No booth polygons for hall ${hall}.` });
      } else {
        setHallGeo({ status: "ready", data: booths });
      }
    } catch (error) {
      setHallGeo(toLoadError(error, `Unable to load hall ${hall} geometry`));
    }
    try {
      const aisles = await fetchHallAisles(hall);
      setAisleState({ status: "ready", data: aisles });
    } catch (error) {
      const status = error instanceof ApiError ? error.status : undefined;
      if (status === 404) {
        setAisleState({ status: "empty", message: `No aisle graph for hall ${hall}.` });
      } else {
        setAisleState(toLoadError(error, `Unable to load aisle graph for hall ${hall}`));
      }
    }
  }, []);

  const goToBoothOnFloor = useCallback(
    (booth: string, label: string, hall?: string) => {
      const mapHall = resolveMapHall(hall, booth);
      const hit = { booth_id: booth, label, hall: mapHall };
      setDestination(hit);
      setGoingToBooth(formatBoothHit({ booth, label, hall: mapHall }));
      setSelectedBooth(booth);
      if (mapHall && mapHall !== hallId) void loadHallMap(mapHall);
      setCompanyOpen(null);
      setEnterFloorDrawerOpen(false);
      setMainTab("floor");
      if (window.location.pathname !== "/floor") {
        window.history.pushState({ tab: "floor" }, "", "/floor");
      }
      void postPresence({
        display_name: displayName.trim() || signedInName || "Team member",
        event_key: eventKey.trim() || EVENT_KEY,
        destination_booth_id: booth,
        destination_label: label,
        destination_hall: mapHall ?? null,
      }).catch(() => undefined);
    },
    [displayName, eventKey, hallId, loadHallMap, signedInName],
  );

  useEffect(() => {
    if (!getStoredDisplayName() && signedInName) {
      setDisplayName(signedInName);
    }
  }, [signedInName]);

  useEffect(() => {
    if (!signedInOid) {
      setGraphPhotoUrl(null);
      return;
    }
    let revoked = false;
    let objectUrl: string | null = null;
    void fetchGraphPhotoUrl(instance).then((url) => {
      if (revoked) {
        if (url) URL.revokeObjectURL(url);
        return;
      }
      objectUrl = url;
      setGraphPhotoUrl(url);
    });
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [instance, signedInOid, photoTick]);

  useEffect(() => {
    void loadHealth();
    void loadHallMap("A");
    // Demo path on first paint only; later lookups are explicit form actions.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only seed load
  }, []);

  useEffect(() => {
    let cancelled = false;
    void callTool<{ results?: AccountEventRow[] }>("account_event_list", {
      event_key: eventKey.trim() || EVENT_KEY,
    })
      .then((payload) => {
        if (!cancelled) setSharedBoothIds(sharedBoothIdsFromEvents(payload.results ?? []));
      })
      .catch(() => {
        if (!cancelled) setSharedBoothIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [eventKey]);

  const healthData = health.status === "ready" ? health.data : null;
  const hallBooths = hallGeo.status === "ready" ? hallGeo.data : null;
  const hallAisles = aisleState.status === "ready" ? aisleState.data : null;

  const openCompanyPane = useCallback((target: CompanyBrowserTarget) => {
    setCompanyOpen({ target, mode: "pane" });
  }, []);

  const openCompanyModal = useCallback((target: CompanyBrowserTarget) => {
    setCompanyOpen({ target, mode: "modal" });
  }, []);

  const closeCompany = useCallback(() => {
    setCompanyOpen(null);
  }, []);

  const switchCompanyAccount = useCallback((next: CompanyBrowserTarget) => {
    setCompanyOpen((prev) => ({
      mode: prev?.mode ?? "pane",
      target: {
        account_id: next.account_id,
        booth_number: next.booth_number || prev?.target.booth_number,
        tab: next.tab ?? prev?.target.tab,
      },
    }));
  }, []);

  const paneOpen = mainTab === "account" && companyOpen?.mode === "pane";
  const accountCreateOpen = paneOpen && Boolean(companyOpen?.target.create);
  const modalOpen = mainTab !== "account" && companyOpen?.mode === "modal";
  const contactPaneOpen = mainTab === "contacts" && contactOpen?.mode === "pane";
  const contactModalOpen = contactOpen?.mode === "modal";
  const rosterRecordOpen =
    (mainTab === "account" && Boolean(companyOpen)) ||
    (mainTab === "contacts" && Boolean(contactOpen || companyOpen));

  const openContactPane = useCallback((target: ContactBrowserTarget) => {
    setContactOpen({ target, mode: "pane" });
  }, []);

  const openContactModal = useCallback((target: ContactBrowserTarget) => {
    setContactOpen({ target, mode: "modal" });
  }, []);

  const closeContact = useCallback(() => {
    setContactOpen(null);
  }, []);

  const openContactFromApp = useCallback(
    (target: ContactBrowserTarget) => {
      if (mainTab === "contacts") {
        openContactPane(target);
      } else {
        openContactModal(target);
      }
    },
    [mainTab, openContactPane, openContactModal],
  );

  const healthTone = useMemo(() => {
    if (health.status === "loading") return "idle";
    if (health.status === "error") return "err";
    if (healthData?.status === "ok" && healthData.db !== false) return "ok";
    return "warn";
  }, [health, healthData]);

  const floorMode = mainTab === "floor";
  const filterActive = floorMode ? floorFiltersAreActive(filters) : boardFiltersAreActive(filters);
  const filterCount = floorMode ? floorFilterCount(filters) : boardFilterCount(filters);
  const rosterMode = mainTab === "account" || mainTab === "contacts" ? mainTab : null;
  const { total: rosterTotal, pending: rosterTotalPending } = useRosterMatchTotal(
    rosterMode,
    eventKey.trim() || EVENT_KEY,
    campaignKey,
    actor,
    filters,
    contactQuery,
  );
  const rosterPlaceholderCount =
    rosterTotalPending || rosterTotal == null
      ? null
      : rosterTotal;

  useEffect(() => {
    setFilterOpen(false);
  }, [mainTab]);

  useEffect(() => {
    if (rosterRecordOpen && filterOpen) setFilterOpen(false);
  }, [filterOpen, rosterRecordOpen]);

  const canShowAppDownload = !isNativeShell() && canOfferAppDownload();
  const handleAppDownload = useCallback(() => {
    void requestAppDownload();
  }, []);

  const ratingsBoard = (
    <RatingsBoard
      eventKey={eventKey.trim() || EVENT_KEY}
      actor={actor}
      campaignKey={campaignKey}
      filters={filters}
      reloadKey={planEpoch}
      compact={Boolean(paneOpen)}
      selectedAccountId={paneOpen && !accountCreateOpen ? companyOpen?.target.account_id ?? null : null}
      onOpenCompany={openCompanyPane}
      onGoToBooth={goToBoothOnFloor}
      sharedBoothIds={sharedBoothIds}
    />
  );

  const topbar = (
    <header className="topbar">
        <h1 className="visually-hidden">Account workspace</h1>
        <div className="topbar-cluster">
          <div className="topbar-logo-row">
            <img className="topbar-logo" src={logoUrl} alt="Charm" />
          </div>
        </div>
        <MainNav
          mainTab={mainTab}
          onSelectTab={selectTab}
          photoUrl={graphPhotoUrl}
          displayName={signedInName || account?.username || "Account"}
          onAppDownload={canShowAppDownload ? handleAppDownload : undefined}
          onSignOut={() => void startLogout(instance)}
          healthTone={healthTone}
          healthLabel={`Health ${health.status === "ready" ? healthData?.status ?? "unknown" : health.status}`}
          healthTitle={
            health.status === "ready"
              ? `v${healthData?.version ?? "?"} · db ${yesNo(healthData?.db)} · blob ${yesNo(healthData?.blob)} · mkt ${yesNo(healthData?.mkt_schema)}`
              : health.status
          }
          onRefreshHealth={() => void loadHealth()}
          scannerOpen={scannerOpen}
          askAiOpen={askAiOpen}
          onScanBadge={() => setScannerOpen(true)}
          onAskAi={() => setAskAiOpen(true)}
        />
        {mainTab !== "profile" ? <WorkspaceTabs mainTab={mainTab} onSelectTab={selectTab} /> : null}
    </header>
  );

  return (
    <div
      className={`app-shell${mainTab === "floor" ? " floor-mode" : ""}${paneOpen || contactPaneOpen ? " account-split" : ""}`}
    >
      {topbar}

      {mainTab !== "profile" && mainTab !== "campaigns" && !rosterRecordOpen ? (
        <div className="workspace-toolbar">
          <div className="workspace-toolbar-start">
            <div className="workspace-search">
              {mainTab === "account" ? (
                <SearchTile
                  value={filters.query}
                  onChange={(query) => setFilters({ ...filters, query })}
                  hintId="account-search-hint"
                  hintLabel="Searches Account."
                  fields={["Account"]}
                  autoCloseHintOnInput
                  placeholder={
                    rosterPlaceholderCount == null
                      ? "Search accounts ..."
                      : formatRosterSearchPlaceholder("accounts", rosterPlaceholderCount)
                  }
                  pending={rosterTotalPending}
                />
              ) : mainTab === "contacts" ? (
                <SearchTile
                  value={contactQuery}
                  onChange={setContactQuery}
                  hintId="contacts-search-hint"
                  hintLabel="Searches Contact name, Account name, Email, Title, and Event Contact ID."
                  fields={["Contact name", "Account name", "Email", "Title", "Event Contact ID"]}
                  autoCloseHintOnInput
                  placeholder={
                    rosterPlaceholderCount == null
                      ? "Search contacts ..."
                      : formatRosterSearchPlaceholder("contacts", rosterPlaceholderCount)
                  }
                  pending={contactSearching || rosterTotalPending}
                />
              ) : (
                <BoothSearchField
                  id="floor-account-search"
                  eventKey={eventKey.trim() || EVENT_KEY}
                  campaignKey={campaignKey}
                  value={floorSearch}
                  onChange={setFloorSearch}
                  placeholder="Search ..."
                  onPick={(hit) => {
                    if (floorSearchPickRef.current) {
                      floorSearchPickRef.current(hit);
                      return;
                    }
                    goToBoothOnFloor(hit.booth, hit.label, hit.hall);
                  }}
                />
              )}
            </div>
            <select
              className="campaign-chooser"
              aria-label="Event"
              value={campaignKey}
              onChange={(e) => setCampaignKey(e.target.value)}
            >
              {CAMPAIGNS.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
            </select>
            <div className="workspace-filter-controls">
              <FilterFunnelButton
                open={filterOpen}
                active={filterActive}
                count={filterCount}
                onClick={() => setFilterOpen((current) => !current)}
              />
              <FilterClearButton
                active={filterActive}
                onClick={() => {
                  setFilters(clearBoardFilters(filters));
                  setFilterOpen(false);
                }}
              />
            </div>
          </div>
          {mainTab === "contacts" || mainTab === "account" ? (
            <div className="workspace-toolbar-end">
              {mainTab === "contacts" ? (
                <WorkspaceNewButton onClick={() => openContactPane({ create: true })} />
              ) : (
                <WorkspaceNewButton onClick={() => openCompanyPane({ create: true })} />
              )}
            </div>
          ) : null}
          {mainTab === "account" || mainTab === "contacts" ? (
            <WorkspaceActiveFilters filters={filters} onChange={setFilters} />
          ) : null}
        </div>
      ) : null}

      {(mainTab === "account" || mainTab === "contacts") && !rosterRecordOpen ? (
        <FloorFilterDrawer
          open={filterOpen}
          onOpenChange={setFilterOpen}
          filters={filters}
          onFiltersChange={setFilters}
          includeOwner
          eventKey={eventKey.trim() || EVENT_KEY}
        />
      ) : null}

      {mainTab === "profile" ? (
        <ProfileScreen
          displayName={signedInName || "Microsoft account"}
          email={account?.username ?? ""}
          photoUrl={graphPhotoUrl}
          presence={selfPresence(presence, { userId: signedInOid, email: actor })}
          eventKey={eventKey.trim() || EVENT_KEY}
          campaignKey={campaignKey}
          signedInOid={signedInOid}
          onBack={() => selectTab("account")}
          onPhotoChanged={() => setPhotoTick((value) => value + 1)}
          onCheckedIn={(hit) => {
            const mapHall = resolveMapHall(hit.hall, hit.booth);
            setCheckInBooth(hit.booth);
            setCheckInQuery(formatBoothHit(hit));
            setSelectedBooth(hit.booth);
            if (mapHall && mapHall !== hallId) void loadHallMap(mapHall);
          }}
        />
      ) : mainTab === "account" ? (
        <div className={`account-workspace${paneOpen ? " is-split" : ""}`}>
          {paneOpen ? <div className="account-nav">{ratingsBoard}</div> : ratingsBoard}
          {paneOpen && companyOpen ? (
            <div className="account-content">
              {accountCreateOpen ? (
                <AccountCreateScreen
                  variant="pane"
                  eventKey={eventKey.trim() || EVENT_KEY}
                  actor={actor}
                  onClose={closeCompany}
                  onCreated={(accountId) => {
                    setPlanEpoch((value) => value + 1);
                    setCompanyOpen({ mode: "pane", target: { account_id: accountId } });
                  }}
                />
              ) : (
                <CompanyBrowserScreen
                  variant="pane"
                  eventKey={eventKey.trim() || EVENT_KEY}
                  campaignKey={campaignKey}
                  target={companyOpen.target}
                  actor={actor}
                  onClose={closeCompany}
                  onSwitchAccount={switchCompanyAccount}
                  onSetDestination={goToBoothOnFloor}
                  onClosedOut={() => setPlanEpoch((value) => value + 1)}
                  onOpenContact={(target) => {
                    selectTab("contacts");
                    openContactPane(target);
                  }}
                />
              )}
            </div>
          ) : null}
        </div>
      ) : mainTab === "contacts" ? (
        <div className={`contacts-workspace${contactPaneOpen ? " is-split" : ""}`}>
          <div className="contacts-nav">
            <ContactsScreen
              eventKey={eventKey.trim() || EVENT_KEY}
              actor={actor}
              campaignKey={campaignKey}
              filters={filters}
              query={contactQuery}
              reloadKey={contactsEpoch}
              onSearchingChange={setContactSearching}
              onOpenCompany={openCompanyModal}
              onGoToBooth={goToBoothOnFloor}
              sharedBoothIds={sharedBoothIds}
              onOpenContact={(row) => openContactPane({ contact_id: row.contact_id })}
              selectedContactId={contactPaneOpen ? contactOpen?.target.contact_id ?? null : null}
            />
          </div>
          {contactPaneOpen && contactOpen ? (
            <div className="account-content">
              <ContactBrowserScreen
                variant="pane"
                eventKey={eventKey.trim() || EVENT_KEY}
                campaignKey={campaignKey}
                target={contactOpen.target}
                actor={actor}
                onClose={closeContact}
                onOpenCompany={openCompanyModal}
                onSaved={() => setContactsEpoch((value) => value + 1)}
                onCreated={() => {
                  setContactsEpoch((value) => value + 1);
                }}
              />
            </div>
          ) : null}
        </div>
      ) : mainTab === "campaigns" ? (
        <div className="campaigns-workspace">
          <CampaignBuilderApp
            crmNavigation={{
              onOpenContact: openContactFromApp,
              onOpenCompany: openCompanyModal,
            }}
          />
        </div>
      ) : (
        <FloorWorkspace
          eventKey={eventKey.trim() || EVENT_KEY}
          campaignKey={campaignKey}
          actor={actor}
          signedInOid={signedInOid}
          signedInName={signedInName}
          hallId={hallId}
          hallGeo={hallGeo}
          hallBooths={hallBooths}
          hallAisles={hallAisles}
          selectedBooth={selectedBooth}
          originBooth={checkInBooth}
          checkInQuery={checkInQuery}
          onCheckInQuery={setCheckInQuery}
          onOriginBooth={setCheckInBooth}
          destination={destination}
          goingToBooth={goingToBooth}
          onGoingToBooth={setGoingToBooth}
          onDestination={setDestination}
          displayName={displayName}
          presence={presence}
          presenceConnected={presenceConnected}
          initialDrawerOpen={enterFloorDrawerOpen}
          onHallChange={(hall) => void loadHallMap(hall)}
          onSelectBooth={(boothNumber) => {
            setSelectedBooth(boothNumber);
            openCompanyModal({ booth_number: boothNumber });
          }}
          visitsEpoch={planEpoch}
          searchPickRef={floorSearchPickRef}
          onOpenCompany={openCompanyModal}
          filters={filters}
          onFiltersChange={setFilters}
          filterDrawerOpen={filterOpen}
          onFilterDrawerOpenChange={setFilterOpen}
          sharedBoothIds={sharedBoothIds}
        />
      )}

      {paneOpen || contactPaneOpen ? null : <AppFooter />}
      {scannerOpen ? (
        <BadgeScannerModal
          eventKey={eventKey.trim() || EVENT_KEY}
          campaignKey={campaignKey}
          actor={actor}
          onClose={() => setScannerOpen(false)}
          onOpenCompany={(target) => {
            setScannerOpen(false);
            if (mainTab === "account") openCompanyPane(target);
            else openCompanyModal(target);
          }}
          onViewInContacts={(query) => {
            setContactQuery(query);
            setScannerOpen(false);
            selectTab("contacts");
          }}
          onOpenContact={(contactId) => {
            setScannerOpen(false);
            selectTab("contacts");
            openContactPane({ contact_id: contactId });
          }}
        />
      ) : null}
      {askAiOpen ? <AskAiModal onClose={() => setAskAiOpen(false)} /> : null}
      {modalOpen && companyOpen ? (
        <CompanyBrowserScreen
          variant="modal"
          eventKey={eventKey.trim() || EVENT_KEY}
          campaignKey={campaignKey}
          target={companyOpen.target}
          actor={actor}
          onClose={closeCompany}
          onSwitchAccount={switchCompanyAccount}
          onSetDestination={goToBoothOnFloor}
          onClosedOut={() => setPlanEpoch((value) => value + 1)}
          onOpenContact={openContactFromApp}
        />
      ) : null}
      {contactModalOpen && contactOpen ? (
        <ContactBrowserScreen
          variant="modal"
          eventKey={eventKey.trim() || EVENT_KEY}
          campaignKey={campaignKey}
          target={contactOpen.target}
          actor={actor}
          onClose={closeContact}
          onOpenCompany={(target) => {
            closeContact();
            if (mainTab === "account") openCompanyPane(target);
            else openCompanyModal(target);
          }}
          onSaved={() => setContactsEpoch((value) => value + 1)}
          onCreated={() => {
            setContactsEpoch((value) => value + 1);
          }}
        />
      ) : null}
    </div>
  );
}

function yesNo(value: boolean | undefined): string {
  if (value === true) return "ok";
  if (value === false) return "down";
  return "n/a";
}
